import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import classNames from 'classnames';
import {
   AtlSpinnerProps,
   SpinnerAlignment,
   SpinnerVariant,
   SpinnerSize,
   SpinnerType,
} from './props';
import { enumAttributeConverter } from '../enum-types';
import { getGlobalStyleSheets } from '../../global-styles';

import * as TagNames from './spinner.tagnames';
import { styleMap } from 'lit/directives/style-map.js';

@customElement(TagNames.Spinner)
export class AtlSpinner extends LitElement implements AtlSpinnerProps {
   declare internalClassName?: string;
   declare spinnerType?: SpinnerType;
   declare size: SpinnerSize;
   declare align?: SpinnerAlignment;
   declare variant?: SpinnerVariant;

   static properties = {
      internalClassName: { type: String },
      spinnerType: {
         attribute: 'spinner-type',
         type: String,
         converter: enumAttributeConverter(SpinnerType, SpinnerType.Border),
      },
      size: {
         type: String,
         converter: enumAttributeConverter(SpinnerSize, SpinnerSize.small),
      },
      align: {
         type: String,
         converter: enumAttributeConverter(
            SpinnerAlignment,
            SpinnerAlignment.TopCenter
         ),
      },
      variant: {
         type: String,
         converter: enumAttributeConverter(
            SpinnerVariant,
            SpinnerVariant.Primary
         ),
      },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .spinner-border {
            border-top-style: solid;
            border-top-width: 0.3rem;
            border-right-style: solid;
            border-right-width: 0.3rem;
            border-bottom-style: solid;
            border-bottom-width: 0.3rem;
            border-left-style: solid;
            border-left-width: 0.3rem;
            border-right-color: transparent;
         }
      `,
   ];

   private get _size() {
      switch (this.size) {
         case SpinnerSize.larger:
            return 4;
         case SpinnerSize.large:
            return 3;
         case SpinnerSize.small:
            return 1;
         case SpinnerSize.medium:
         default:
            return 2;
      }
   }

   private get _sizeWidth() {
      switch (this.size) {
         case SpinnerSize.larger:
         case SpinnerSize.large:
            return 0.3;
         case SpinnerSize.small:
            return 0.15;
         case SpinnerSize.medium:
         default:
            return 0.2;
      }
   }

   private _spinnerAlignmentClass = () => {
      switch (this.align) {
         case SpinnerAlignment.TopCenter:
            return 'd-flex align-items-start justify-content-center';
         case SpinnerAlignment.Middle:
            return 'd-flex align-items-center justify-content-center';
         default:
            return '';
      }
   };

   private _spinnerType = () => {
      switch (this.spinnerType) {
         case SpinnerType.Growing:
            return 'spinner-grow';
         default:
            return 'spinner-border';
      }
   };

   private _renderSpinner = () => {
      const spinnerStyles = {
         width: `${this._size}rem`,
         height: `${this._size}rem`,
         borderWidth: `${this._sizeWidth}rem`,
      };

      return html`
         <div
            class=${classNames(
               this._spinnerType(),
               `text-${this.variant ?? SpinnerVariant.Primary}`,
               this.internalClassName
            )}
            role="status"
            part="spinner"
            style=${styleMap(spinnerStyles)}
         >
            <span class="visually-hidden">Loading...</span>
         </div>
      `;
   };

   render() {
      return html`
         <div
            class=${classNames(
               'w-100 h-100 d-flex',
               this._spinnerAlignmentClass()
            )}
         >
            ${this._renderSpinner()}
         </div>
      `;
   }
}
